import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabase';
import { createVCard } from '../utils/vcard';
import type { Database } from '../lib/database.types';

type QRCodeData = Database['public']['Tables']['qr_codes']['Row'];
type UserSettings = Database['public']['Tables']['user_settings']['Row'];
type Folder = Database['public']['Tables']['folders']['Row'];

interface ContactInfo {
  name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  photo?: string;
  additionalPhones?: string[];
  socialMedia?: {
    facebook?: string;
    linkedin?: string;
    instagram?: string;
    twitter?: string;
  };
  websites?: {
    personal?: string;
    work?: string;
    blog?: string;
    portfolio?: string;
    other?: string[];
  };
  notes?: string;
}

interface QRCodeFormProps {
  darkMode: boolean;
  onQRCodeCreated: () => Promise<void>;
  editingQRCode?: QRCodeData | null;
  onCancelEdit?: () => void;
  folders?: Folder[];
}

const BASE_NETWORKQR_URL = 'https://www.danny.support/networkqr';

export function QRCodeForm({ darkMode, onQRCodeCreated, editingQRCode, onCancelEdit, folders = [] }: QRCodeFormProps) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isBusinessCard, setIsBusinessCard] = useState(false);
  const [isAffiliateCard, setIsAffiliateCard] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    additionalPhones: [''],
    socialMedia: {
      facebook: '',
      linkedin: '',
      instagram: '',
      twitter: ''
    },
    websites: {
      personal: '',
      work: '',
      blog: '',
      portfolio: '',
      other: ['']
    },
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);

  useEffect(() => {
    loadUserSettings();
  }, []);

  useEffect(() => {
    if (editingQRCode) {
      if (editingQRCode.affiliate_enabled) {
        if (onCancelEdit) onCancelEdit();
        return;
      }
      
      setTitle(editingQRCode.title);
      setUrl(editingQRCode.url);
      setSelectedFolderId(editingQRCode.folder_id);
      setIsBusinessCard(editingQRCode.is_business_card);
      setIsAffiliateCard(editingQRCode.affiliate_enabled || false);
      if (editingQRCode.contact_info) {
        setContactInfo({
          ...editingQRCode.contact_info as ContactInfo,
          additionalPhones: editingQRCode.contact_info.additionalPhones || [''],
          socialMedia: editingQRCode.contact_info.socialMedia || {
            facebook: '',
            linkedin: '',
            instagram: '',
            twitter: ''
          },
          websites: editingQRCode.contact_info.websites || {
            personal: '',
            work: '',
            blog: '',
            portfolio: '',
            other: ['']
          }
        });
      }
    }
  }, [editingQRCode, onCancelEdit]);

  const loadUserSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setUserSettings(data);
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  const handleContactInfoChange = (field: keyof ContactInfo | string, value: any) => {
    setContactInfo(prev => {
      if (field.startsWith('social.')) {
        const socialField = field.split('.')[1] as keyof typeof prev.socialMedia;
        return {
          ...prev,
          socialMedia: {
            ...prev.socialMedia,
            [socialField]: value
          }
        };
      } else if (field.startsWith('websites.')) {
        const websiteField = field.split('.')[1] as keyof typeof prev.websites;
        return {
          ...prev,
          websites: {
            ...prev.websites,
            [websiteField]: value
          }
        };
      } else if (field === 'additionalPhones') {
        return {
          ...prev,
          additionalPhones: value
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleAddPhone = () => {
    setContactInfo(prev => ({
      ...prev,
      additionalPhones: [...(prev.additionalPhones || []), '']
    }));
  };

  const handleRemovePhone = (index: number) => {
    setContactInfo(prev => ({
      ...prev,
      additionalPhones: prev.additionalPhones?.filter((_, i) => i !== index)
    }));
  };

  const handleAddWebsite = () => {
    setContactInfo(prev => ({
      ...prev,
      websites: {
        ...prev.websites,
        other: [...(prev.websites?.other || []), '']
      }
    }));
  };

  const handleRemoveWebsite = (index: number) => {
    setContactInfo(prev => ({
      ...prev,
      websites: {
        ...prev.websites,
        other: prev.websites?.other?.filter((_, i) => i !== index) || []
      }
    }));
  };

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!editingQRCode && isAffiliateCard) {
        const { data: existingAffiliateCard } = await supabase
          .from('qr_codes')
          .select('*')
          .eq('affiliate_enabled', true)
          .single();

        if (existingAffiliateCard) {
          throw new Error('You already have a NetworkQR affiliate card');
        }
      }

      let finalUrl = url;
      if (isBusinessCard) {
        finalUrl = createVCard(contactInfo);
      } else if (isAffiliateCard) {
        finalUrl = userSettings?.affiliate_code 
          ? `${BASE_NETWORKQR_URL}?aff=${userSettings.affiliate_code}`
          : BASE_NETWORKQR_URL;
      }

      const qrData = await QRCode.toDataURL(finalUrl);
      const userId = (await supabase.auth.getUser()).data.user?.id;

      if (!userId) throw new Error('No user ID found');

      if (editingQRCode) {
        const { error: updateError } = await supabase
          .from('qr_codes')
          .update({
            title,
            url: finalUrl,
            qr_data: qrData,
            is_business_card: isBusinessCard,
            affiliate_enabled: isAffiliateCard,
            contact_info: isBusinessCard ? contactInfo : null,
            folder_id: selectedFolderId
          })
          .eq('id', editingQRCode.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('qr_codes')
          .insert([{
            user_id: userId,
            title,
            url: finalUrl,
            qr_data: qrData,
            is_business_card: isBusinessCard,
            affiliate_enabled: isAffiliateCard,
            contact_info: isBusinessCard ? contactInfo : null,
            folder_id: selectedFolderId
          }]);

        if (insertError) throw insertError;
      }

      setTitle('');
      setUrl('');
      setSelectedFolderId(null);
      setIsBusinessCard(false);
      setIsAffiliateCard(false);
      setContactInfo({
        name: '',
        email: '',
        phone: '',
        company: '',
        title: '',
        additionalPhones: [''],
        socialMedia: {
          facebook: '',
          linkedin: '',
          instagram: '',
          twitter: ''
        },
        websites: {
          personal: '',
          work: '',
          blog: '',
          portfolio: '',
          other: ['']
        },
        notes: ''
      });

      await onQRCodeCreated();
      if (onCancelEdit) onCancelEdit();
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); generateQRCode(); }} className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isBusinessCard"
              checked={isBusinessCard}
              onChange={(e) => {
                setIsBusinessCard(e.target.checked);
                if (e.target.checked) setIsAffiliateCard(false);
              }}
              className="mr-2"
            />
            <label
              htmlFor="isBusinessCard"
              className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Business Card
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isAffiliateCard"
              checked={isAffiliateCard}
              onChange={(e) => {
                setIsAffiliateCard(e.target.checked);
                if (e.target.checked) {
                  setIsBusinessCard(false);
                  setUrl(userSettings?.affiliate_code 
                    ? `${BASE_NETWORKQR_URL}?aff=${userSettings.affiliate_code}`
                    : BASE_NETWORKQR_URL
                  );
                }
              }}
              className="mr-2"
            />
            <label
              htmlFor="isAffiliateCard"
              className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Affiliate Card
            </label>
          </div>
        </div>

        <div>
          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`mt-1 block w-full rounded-md border px-3 py-2 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
            required
          />
        </div>

        <div>
          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Folder (Optional)
          </label>
          <select
            value={selectedFolderId || ''}
            onChange={(e) => setSelectedFolderId(e.target.value || null)}
            className={`mt-1 block w-full rounded-md border px-3 py-2 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
          >
            <option value="">No Folder</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </div>

        {isBusinessCard ? (
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Name
              </label>
              <input
                type="text"
                value={contactInfo.name}
                onChange={(e) => handleContactInfoChange('name', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Email
              </label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleContactInfoChange('email', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Primary Phone
              </label>
              <input
                type="tel"
                value={contactInfo.phone}
                onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>

            {/* Additional Phone Numbers */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Additional Phone Numbers
              </label>
              {contactInfo.additionalPhones?.map((phone, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      const newPhones = [...(contactInfo.additionalPhones || [])];
                      newPhones[index] = e.target.value;
                      handleContactInfoChange('additionalPhones', newPhones);
                    }}
                    className={`flex-grow rounded-md border px-3 py-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                    placeholder="Additional phone number"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhone(index)}
                    className="px-3 py-2 text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddPhone}
                className={`mt-2 text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'} hover:text-blue-700`}
              >
                + Add Phone Number
              </button>
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Company
              </label>
              <input
                type="text"
                value={contactInfo.company}
                onChange={(e) => handleContactInfoChange('company', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Title
              </label>
              <input
                type="text"
                value={contactInfo.title}
                onChange={(e) => handleContactInfoChange('title', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Profile Photo (Optional)
              </label>
              <div className="mt-1 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        handleContactInfoChange('photo', reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className={`block w-full text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                />
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Or enter image URL:
                </p>
                <input
                  type="url"
                  value={contactInfo.photo?.startsWith('http') ? contactInfo.photo : ''}
                  onChange={(e) => handleContactInfoChange('photo', e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                  className={`block w-full rounded-md border px-3 py-2 text-sm ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                {contactInfo.photo && (
                  <div className="mt-2">
                    <img
                      src={contactInfo.photo}
                      alt="Profile preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => handleContactInfoChange('photo', '')}
                      className={`mt-1 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'} hover:underline`}
                    >
                      Remove photo
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Fields */}
            <div className="space-y-3">
              <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Social Media
              </h3>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={contactInfo.socialMedia?.linkedin || ''}
                  onChange={(e) => handleContactInfoChange('social.linkedin', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Facebook
                </label>
                <input
                  type="url"
                  value={contactInfo.socialMedia?.facebook || ''}
                  onChange={(e) => handleContactInfoChange('social.facebook', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://facebook.com/username"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Instagram
                </label>
                <input
                  type="url"
                  value={contactInfo.socialMedia?.instagram || ''}
                  onChange={(e) => handleContactInfoChange('social.instagram', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Twitter
                </label>
                <input
                  type="url"
                  value={contactInfo.socialMedia?.twitter || ''}
                  onChange={(e) => handleContactInfoChange('social.twitter', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://twitter.com/username"
                />
              </div>
            </div>

            {/* Websites Section */}
            <div className="space-y-3">
              <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Websites
              </h3>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Personal Website
                </label>
                <input
                  type="url"
                  value={contactInfo.websites?.personal || ''}
                  onChange={(e) => handleContactInfoChange('websites.personal', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Work Website
                </label>
                <input
                  type="url"
                  value={contactInfo.websites?.work || ''}
                  onChange={(e) => handleContactInfoChange('websites.work', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://company.com"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Blog
                </label>
                <input
                  type="url"
                  value={contactInfo.websites?.blog || ''}
                  onChange={(e) => handleContactInfoChange('websites.blog', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://blog.example.com"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Portfolio
                </label>
                <input
                  type="url"
                  value={contactInfo.websites?.portfolio || ''}
                  onChange={(e) => handleContactInfoChange('websites.portfolio', e.target.value)}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://portfolio.example.com"
                />
              </div>
              
              {/* Additional Websites */}
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Additional Websites
                </label>
                {contactInfo.websites?.other?.map((url, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => {
                        const newUrls = [...(contactInfo.websites?.other || [])];
                        newUrls[index] = e.target.value;
                        handleContactInfoChange('websites.other', newUrls);
                      }}
                      className={`flex-grow rounded-md border px-3 py-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="https://example.com"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveWebsite(index)}
                      className="px-3 py-2 text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddWebsite}
                  className={`mt-2 text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'} hover:text-blue-700`}
                >
                  + Add Website
                </button>
              </div>
            </div>

            {/* Notes Field */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Notes
              </label>
              <textarea
                value={contactInfo.notes || ''}
                onChange={(e) => handleContactInfoChange('notes', e.target.value)}
                className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                rows={3}
                placeholder="Add any additional notes..."
              />
            </div>
          </div>
        ) : !isAffiliateCard ? (
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              URL
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={`mt-1 block w-full rounded-md border px-3 py-2 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
              required
            />
          </div>
        ) : (
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Affiliate URL
            </label>
            <input
              type="text"
              value={url}
              readOnly
              className={`mt-1 block w-full rounded-md border px-3 py-2 bg-gray-100 ${
                darkMode ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-300'
              }`}
            />
            {!userSettings?.affiliate_code && (
              <p className={`mt-1 text-sm text-gray-500`}>
                Using base NetworkQR URL. Set your affiliate code in profile to earn commissions.
              </p>
            )}
          </div>
        )}

        <div className="flex gap-3">
          {editingQRCode && (
            <button
              type="button"
              onClick={onCancelEdit}
              className={`flex-1 px-4 py-2 rounded-md ${
                darkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className={`flex-1 bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 disabled:opacity-50`}
          >
            {loading ? 'Saving...' : editingQRCode ? 'Update QR Code' : 'Generate QR Code'}
          </button>
        </div>
      </form>
    </div>
  );
}